import { StaffDesignBoundary } from "./components/StaffDesignBoundary";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api/client";
import type { AuthSession, HealthStatus, SyncStatus } from "./api/types";
import { AuthGate } from "./components/Authentication";
import { ApiStatusBadge, SyncStatusBadge } from "./components/StatusBadge";
import { FeedbackLauncher } from "./components/FeedbackLauncher";
import { DeviceDeliveryAlertBanner } from "./components/DeviceDeliveryAlertBanner";
import { FinanceCapabilitiesOverrideProvider, useFinanceCapabilities } from "./hooks/useFinanceCapabilities";
import { useAdminScreenPreview } from "./hooks/useAdminScreenPreview";
import { AppIcon, type AppIconName } from "./components/AppIcon";
import { isHomeStyleRegistrationHash } from "./homeStyleRegistrationRoute";
import {
  CONSTRUCTION_PROJECT_CONTEXT_CHANGE_EVENT,
  homeStyleWorkspaceHrefForConstructionHash,
  integratedWorkspaceHref,
} from "./integratedRoute";
import { WorkspaceTopbar } from "./components/WorkspaceTopbar";
import { FieldWorkApp, isHomeStyleFieldRole } from "./components/FieldWorkApp";
import { canAccessDedicatedPayouts } from "./utils/payoutAccess";
import { seoulCalendarDate } from "./utils/customerReceivables";
import { financeViewForRoute } from "./utils/financeViewRoute";
import "./project-selmo-design-system.css";

const ProjectWorkspace = lazy(() => import("./pages/ProjectWorkspace"));
const ReceiptsPage = lazy(() => import("./pages/ReceiptsPage"));
const PayoutsPage = lazy(() => import("./pages/PayoutsPage"));
const HomeDashboardPage = lazy(() => import("./pages/HomeDashboardPage"));
const ConstructionMyWorkPage = lazy(() => import("./pages/ConstructionMyWorkPage"));
const MobileSchedulePage = lazy(() => import("./pages/MobileSchedulePage"));
const ConstructionFinanceWorkspace = lazy(() => import("./pages/ConstructionFinanceWorkspace"));
const MonthlyProfitPage = lazy(() => import("./pages/MonthlyProfitPage"));
const ApprovalsPage = lazy(() => import("./pages/ApprovalsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const OperationsPage = lazy(() => import("./pages/OperationsPage"));
const SmsPage = lazy(() => import("./pages/SmsPage"));
const ImprovementCenterPage = lazy(() => import("./pages/ImprovementCenterPage"));

const AsExpensesPage = lazy(() => import("./pages/AsExpensesPage"));

type RouteId = "dashboard" | "my-work" | "projects" | "schedule" | "estimates" | "approvals" | "payments" | "payouts" | "as-expenses" | "finance" | "banking" | "tax" | "payroll" | "sms" | "reports" | "operations" | "improvements" | "admin";

type NavigationItem = {
  id: RouteId;
  icon: AppIconName;
  label: string;
  mobileLabel?: string;
  mobilePrimary: boolean;
  mobileOnly?: boolean;
  adminOnly?: boolean;
};

const NAVIGATION: NavigationItem[] = [
  { id: "dashboard", icon: "dashboard", label: "대시보드", mobileLabel: "홈", mobilePrimary: true },
  { id: "my-work", icon: "tasks", label: "내 업무", mobilePrimary: true },
  { id: "projects", icon: "projects", label: "프로젝트", mobilePrimary: true },
  { id: "schedule", icon: "schedule", label: "일정", mobilePrimary: true },
  { id: "estimates", icon: "reports", label: "견적", mobilePrimary: false },
  { id: "approvals", icon: "approvals", label: "승인", mobilePrimary: false, adminOnly: true },
  { id: "payments", icon: "payments", label: "고객입금", mobilePrimary: false },
  { id: "payouts", icon: "payouts", label: "업체지급", mobilePrimary: false },
  { id: "as-expenses", icon: "work", label: "AS·추가비용", mobilePrimary: false },
  { id: "finance", icon: "payments", label: "자금관리", mobilePrimary: false },
  { id: "sms", icon: "sms", label: "문자함", mobilePrimary: false },
  { id: "reports", icon: "reports", label: "월간보고", mobilePrimary: false },
  { id: "operations", icon: "operations", label: "운영", mobilePrimary: false },
  { id: "improvements", icon: "improvements", label: "개선센터", mobilePrimary: false },
  { id: "admin", icon: "admin", label: "관리", mobilePrimary: false, adminOnly: true },
];

const LEGACY_FINANCE_DETAIL_ROUTES = new Map<RouteId, string>([
  ["banking", "#/finance?view=bank"],
  ["tax", "#/finance?view=tax"],
  ["payroll", "#/finance?view=staff"],
]);
const FINANCE_ADMIN_HIDDEN_ROUTES = new Set<RouteId>(["approvals", "operations"]);
const ALL_ROUTE_IDS = new Set<RouteId>([
  ...NAVIGATION.map((item) => item.id),
  ...LEGACY_FINANCE_DETAIL_ROUTES.keys(),
]);

const DESKTOP_NAV_GROUPS: { label?: string; ids: RouteId[] }[] = [
  { ids: ["dashboard", "my-work", "projects", "schedule", "estimates"] },
  { label: "정산", ids: ["approvals", "payments", "payouts", "as-expenses", "finance", "sms"] },
  { label: "보고/운영", ids: ["reports", "operations"] },
];
const MOBILE_ROUTE_SUBTITLE: Record<RouteId, string> = {
  dashboard: "오늘의 업무",
  "my-work": "배정 업무",
  projects: "프로젝트 찾기",
  schedule: "주간 일정",
  estimates: "견적 관리",
  approvals: "견적 최종 승인",
  payments: "고객 입금",
  payouts: "업체 지급",
  "as-expenses": "견적 외 비용",
  finance: "자금 관리",
  banking: "통장 관리",
  tax: "부가세 처리",
  payroll: "직원 월급",
  sms: "고객 문자",
  reports: "월간 보고",
  operations: "운영",
  improvements: "개선센터",
  admin: "관리",
};

function isFinanceAdministrator(role: AuthSession["role"]): boolean {
  return role === "FINANCE_ADMIN";
}

function financeAdministratorRouteReplacement(route: RouteId): string | undefined {
  if (FINANCE_ADMIN_HIDDEN_ROUTES.has(route)) return "#/dashboard";
  return LEGACY_FINANCE_DETAIL_ROUTES.get(route);
}

function currentRoute(role?: AuthSession["role"]): RouteId {
  const candidate = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  if (isFinanceAdministrator(role)) {
    const route = candidate as RouteId;
    if (FINANCE_ADMIN_HIDDEN_ROUTES.has(route)) return "dashboard";
    if (LEGACY_FINANCE_DETAIL_ROUTES.has(route)) return "finance";
  }
  return ALL_ROUTE_IDS.has(candidate as RouteId) ? candidate as RouteId : "dashboard";
}

function PageFallback() {
  return (
    <main className="page-loading" aria-label="화면 불러오는 중">
      <i className="spinner" />
      <span>업무 화면을 불러오고 있어요…</span>
    </main>
  );
}

interface WorkspaceProps {
  session: AuthSession;
  loggingOut: boolean;
  onLogout: () => Promise<void>;
}

function HomeStyleAccessDenied() {
  return (
    <main className="home-style-root-gate project-selmo-design" aria-label="홈스타일 접근 권한 없음">
      <section className="card empty-state" role="alert">
        <span className="empty-state__icon">!</span>
        <strong>홈스타일 접근 권한을 확인할 수 없습니다</strong>
        <p>활성 직원 계정 연결 상태를 다시 확인해주세요. 준비 중 안내로 우회하지 않고 접근을 안전하게 차단했습니다.</p>
        <a className="button button--secondary" href={integratedWorkspaceHref("construction")}>시공 대시보드로 돌아가기</a>
      </section>
    </main>
  );
}

export function ProtectedHomeStyleRegistration({ session }: WorkspaceProps) {
  const canRedirect = session.role === "ADMIN"
    && session.capabilities?.includes("INTEGRATED_SHELL_READ") === true;

  useEffect(() => {
    if (!canRedirect) return;
    // The integrated shell re-verifies the server capability and kill switch before
    // rendering the canonical registration screen.
    window.location.replace("/integrated/home-style?screen=registration");
  }, [canRedirect]);

  if (!canRedirect) return <HomeStyleAccessDenied />;
  return <PageFallback />;
}

export function OperationalWorkspace({ session: actualSession, loggingOut, onLogout }: WorkspaceProps) {
  const screenPreview = useAdminScreenPreview(actualSession);
  const session = screenPreview.effectiveSession;
  const financeAdministrator = isFinanceAdministrator(session.role);
  const canAccessHomeStyle = session.capabilities?.includes("INTEGRATED_SHELL_READ") === true;
  const [route, setRoute] = useState<RouteId>(() => currentRoute(session.role));
  const [health, setHealth] = useState<HealthStatus>();
  const [healthLoading, setHealthLoading] = useState(true);
  const [sync, setSync] = useState<SyncStatus>();
  const [approvalCount, setApprovalCount] = useState(0);
  const [customerReceiptActionCount, setCustomerReceiptActionCount] = useState(0);
  const [vendorPayoutActionCount, setVendorPayoutActionCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [financeScreenPreview, setFinanceScreenPreview] = useState<"mobile" | "desktop">("desktop");
  const [constructionNavOpen, setConstructionNavOpen] = useState(false);
  const [constructionProjectOpen, setConstructionProjectOpen] = useState(false);
  const [constructionSearch, setConstructionSearch] = useState("");
  const [constructionLocationHash, setConstructionLocationHash] = useState(() => window.location.hash);
  const [constructionRailTarget, setConstructionRailTarget] = useState<HTMLElement | null>(null);
  const healthTimer = useRef<number>();
  const syncTimer = useRef<number>();
  const approvalTimer = useRef<number>();
  const financeActionTimer = useRef<number>();
  const moreTriggerRef = useRef<HTMLButtonElement>(null);
  const moreSheetRef = useRef<HTMLElement>(null);
  const constructionNavRef = useRef<HTMLElement>(null);
  const constructionDrawerReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const { capabilities: liveFinanceCapabilities, loading: liveFinanceCapabilitiesLoading } = useFinanceCapabilities();
  const financeCapabilities = screenPreview.financeCapabilities ?? liveFinanceCapabilities;
  const financeCapabilitiesLoading = screenPreview.financeCapabilities ? false : liveFinanceCapabilitiesLoading;
  const canAccessPayouts = canAccessDedicatedPayouts(financeCapabilities);
  const loadHealth = useCallback(async (signal?: AbortSignal) => {
    try {
      setHealth(await api.health(signal));
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setHealth({ status: "error", database: "disconnected" });
    } finally {
      if (!signal?.aborted) setHealthLoading(false);
    }
  }, []);

  const loadSync = useCallback(async (signal?: AbortSignal) => {
    try {
      setSync(await api.syncStatus(signal));
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setSync({ state: "ERROR", message: "동기화 상태를 확인하지 못했습니다." });
    }
  }, []);

  const loadApprovalCount = useCallback(async (signal?: AbortSignal) => {
    if (session.role !== "ADMIN" || !financeCapabilities.canConfirmMoney) {
      setApprovalCount(0);
      return;
    }
    try {
      const [quotes, asExpenses] = await Promise.all([api.quoteApprovals(signal), api.asExpenses({ filter: "approval" }, signal)]);
      setApprovalCount(quotes.total + asExpenses.items.length);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }, [financeCapabilities.canConfirmMoney, session.role]);

  const loadFinanceActionCounts = useCallback(async (signal?: AbortSignal) => {
    try {
      const overview = await api.dashboardOverview(
        seoulCalendarDate(),
        session.role === "ADMIN" || financeAdministrator ? { view: "all" } : {},
        signal,
      );
      setCustomerReceiptActionCount(overview.summary.actionableReceivableProjectCount);
      setVendorPayoutActionCount(overview.summary.pendingVendorPayoutRequestCount);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }, [financeAdministrator, session.role]);

  const closeConstructionDrawers = useCallback((restoreFocus = true) => {
    setConstructionNavOpen(false);
    setConstructionProjectOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => constructionDrawerReturnFocusRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    const requestedRoute = window.location.hash.replace(/^#\/?/, "").split("?")[0] as RouteId;
    const requestedReplacement = financeAdministrator
      ? financeAdministratorRouteReplacement(requestedRoute)
      : undefined;
    if (!window.location.hash || requestedReplacement) {
      window.history.replaceState(null, "", requestedReplacement ?? "#/dashboard");
    }
    const handleLocationChange = () => {
      const nextRequestedRoute = window.location.hash.replace(/^#\/?/, "").split("?")[0] as RouteId;
      const replacement = financeAdministrator
        ? financeAdministratorRouteReplacement(nextRequestedRoute)
        : undefined;
      if (replacement) {
        window.history.replaceState(null, "", replacement);
      }
      setConstructionLocationHash(window.location.hash);
      setRoute(currentRoute(session.role));
      setMoreOpen(false);
      closeConstructionDrawers(false);
    };
    window.addEventListener("hashchange", handleLocationChange);
    window.addEventListener("popstate", handleLocationChange);
    window.addEventListener(CONSTRUCTION_PROJECT_CONTEXT_CHANGE_EVENT, handleLocationChange);
    return () => {
      window.removeEventListener("hashchange", handleLocationChange);
      window.removeEventListener("popstate", handleLocationChange);
      window.removeEventListener(CONSTRUCTION_PROJECT_CONTEXT_CHANGE_EVENT, handleLocationChange);
    };
  }, [closeConstructionDrawers, financeAdministrator, session.role]);

  useEffect(() => {
    if ((!constructionNavOpen && !constructionProjectOpen) || moreOpen) return;
    const mobileViewport = window.matchMedia("(max-width: 1079px)");
    if (!mobileViewport.matches && financeScreenPreview !== "mobile") {
      closeConstructionDrawers(false);
      return;
    }
    const activeDrawer = constructionNavOpen ? constructionNavRef.current : constructionRailTarget;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () => activeDrawer
      ? [...activeDrawer.querySelectorAll<HTMLElement>(focusableSelector)]
        .filter((item) => !item.hidden && item.getBoundingClientRect().width > 0 && item.getBoundingClientRect().height > 0)
      : [];
    const focusFrame = window.requestAnimationFrame(() => {
      if (!activeDrawer?.contains(document.activeElement)) focusable()[0]?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeConstructionDrawers(true);
        return;
      }
      if (event.key !== "Tab" || !activeDrawer) return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0]!;
      const last = items.at(-1)!;
      if (event.shiftKey && (document.activeElement === first || !activeDrawer.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !activeDrawer.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    const closeOutsideMobile = (event: MediaQueryListEvent) => {
      if (!event.matches && financeScreenPreview !== "mobile") closeConstructionDrawers(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    mobileViewport.addEventListener("change", closeOutsideMobile);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      mobileViewport.removeEventListener("change", closeOutsideMobile);
      document.body.style.overflow = previousOverflow;
    };
  }, [closeConstructionDrawers, constructionNavOpen, constructionProjectOpen, constructionRailTarget, financeScreenPreview, moreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    const mobileViewport = window.matchMedia("(max-width: 768px)");
    if (!mobileViewport.matches) {
      setMoreOpen(false);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    const focusableElements = () => Array.from(
      moreSheetRef.current?.querySelectorAll<HTMLElement>("a[href], button:not(:disabled), select, input, textarea") ?? [],
    ).filter((element) => element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0);
    const focusFrame = window.requestAnimationFrame(() => focusableElements()[0]?.focus());
    const manageDialogKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const closeOutsideMobile = (event: MediaQueryListEvent) => {
      if (!event.matches) setMoreOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", manageDialogKeyboard);
    mobileViewport.addEventListener("change", closeOutsideMobile);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", manageDialogKeyboard);
      mobileViewport.removeEventListener("change", closeOutsideMobile);
      if (moreTriggerRef.current?.getClientRects().length) moreTriggerRef.current.focus();
    };
  }, [moreOpen]);

  useEffect(() => {
    const safeLanding = "#/dashboard";
    if (financeAdministrator && FINANCE_ADMIN_HIDDEN_ROUTES.has(route)) window.location.hash = safeLanding;
    if (route === "admin" && session.role !== "ADMIN") window.location.hash = safeLanding;
    if (route === "approvals" && !financeCapabilitiesLoading
        && (financeAdministrator || !financeCapabilities.canConfirmMoney)) {
      window.location.hash = safeLanding;
    }
    if (route === "operations" && financeAdministrator) window.location.hash = safeLanding;
    if (route === "payouts" && !financeCapabilitiesLoading && !canAccessPayouts) {
      window.location.hash = safeLanding;
    }
    if ((route === "banking" || route === "tax" || route === "payroll")
        && !financeCapabilitiesLoading && !financeCapabilities.canConfirmMoney) {
      window.location.hash = safeLanding;
    }
  }, [canAccessPayouts, financeAdministrator, financeCapabilities.canConfirmMoney, financeCapabilitiesLoading, route, session.role]);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      loadHealth(controller.signal),
      loadSync(controller.signal),
      loadApprovalCount(controller.signal),
      loadFinanceActionCounts(controller.signal),
    ]);
    healthTimer.current = window.setInterval(() => void loadHealth(), 30_000);
    syncTimer.current = window.setInterval(() => void loadSync(), 20_000);
    approvalTimer.current = window.setInterval(() => void loadApprovalCount(), 20_000);
    financeActionTimer.current = window.setInterval(() => void loadFinanceActionCounts(), 20_000);
    return () => {
      controller.abort();
      if (healthTimer.current) window.clearInterval(healthTimer.current);
      if (syncTimer.current) window.clearInterval(syncTimer.current);
      if (approvalTimer.current) window.clearInterval(approvalTimer.current);
      if (financeActionTimer.current) window.clearInterval(financeActionTimer.current);
    };
  }, [loadApprovalCount, loadFinanceActionCounts, loadHealth, loadSync]);

  const logoutAndResetPreview = async () => {
    screenPreview.reset();
    await onLogout();
  };
  const pageProps = { session, loggingOut, onLogout: logoutAndResetPreview };
  const visibleNavigation = NAVIGATION.filter((item) => {
    if (financeAdministrator && (item.id === "approvals" || item.id === "operations")) return false;
    if (item.id === "approvals") return financeCapabilities.canConfirmMoney;
    if (item.id === "payouts") return canAccessPayouts;
    return !item.adminOnly || session.role === "ADMIN";
  });
  const moreActive = visibleNavigation.some((item) => !item.mobilePrimary
    && !["improvements", "admin"].includes(item.id)
    && item.id === route);
  const constructionFinanceRoute = route === "estimates" || route === "finance" || route === "banking" || route === "tax" || route === "payroll" || route === "reports";
  const homeStyleHref = homeStyleWorkspaceHrefForConstructionHash(
    constructionLocationHash,
    window.location.search ?? "",
  );
  if (screenPreview.resolving) return <PageFallback />;
  const workspaceTopbar = (
      <WorkspaceTopbar
        activeWorkspace="construction"
        session={session}
        accountSession={actualSession}
        loggingOut={loggingOut}
        onLogout={logoutAndResetPreview}
        canAccessHomeStyle={canAccessHomeStyle}
        homeStyleHref={homeStyleHref}
        constructionHref="#/dashboard"
        onBlockedHomeStyle={() => { window.location.href = homeStyleHref; }}
        searchLabel="시공 프로젝트 검색"
        searchPlaceholder="시공 프로젝트 검색 · 고객명, 주소, 현장명"
        searchValue={constructionSearch}
        onSearchChange={setConstructionSearch}
        onSearchSubmit={(value) => {
          setConstructionSearch(value);
          window.location.hash = value ? `#/projects?search=${encodeURIComponent(value)}` : "#/projects";
        }}
        onImprovement={() => { window.location.hash = "#/improvements"; }}
        improvementActive={route === "improvements"}
        onAdmin={session.role === "ADMIN" ? () => { window.location.hash = "#/admin"; } : undefined}
        adminActive={route === "admin"}
        screenSwitcher={actualSession.role === "ADMIN" ? {
          value: screenPreview.selectedManagerId,
          options: screenPreview.options,
          loading: screenPreview.loading,
          loadFailed: screenPreview.loadFailed,
          onChange: screenPreview.selectManager,
        } : undefined}
        showWorkspaceSwitch
        showSearch
        showImprovement
        status={<><ApiStatusBadge health={health} loading={healthLoading} /><SyncStatusBadge state={sync?.state} pendingCount={sync?.pendingCount} /></>}
      />
  );
  if (route === 'finance' && financeViewForRoute(route,constructionLocationHash) === 'profit') {
    return <Suspense fallback={<PageFallback />}><MonthlyProfitPage shell={{
      accountName:actualSession.name || actualSession.email || '계정',homeStyleHref,
      navigation:visibleNavigation.map(item=>({label:item.label,href:`#/${item.id}`})),
      searchValue:constructionSearch,onSearchChange:setConstructionSearch,
      onSearchSubmit:value=>{setConstructionSearch(value);window.location.hash=value?`#/projects?search=${encodeURIComponent(value)}`:'#/projects';},
      onLogout:logoutAndResetPreview,loggingOut,
      screenSwitcher:actualSession.role==='ADMIN'?{value:screenPreview.selectedManagerId,options:screenPreview.options,loading:screenPreview.loading,loadFailed:screenPreview.loadFailed,onChange:screenPreview.selectManager}:undefined,
      connectionLabel:healthLoading?'서버 확인 중':health?.status==='ok'?'서버 연결됨':health?.status==='degraded'?'서버 점검 필요':'서버 연결 안 됨',
      connected:health?.status==='ok',syncLabel:sync?.state==='SYNCED'?'동기화 완료':sync?.state==='ERROR'?'동기화 확인 필요':'동기화 상태 확인 중',
    }} /></Suspense>;
  }
  return (
    <div className={`hs-authoritative-root hs-redesign-v2 project-selmo-design${constructionFinanceRoute ? " hs-finance-clone" : ""}`} data-workspace="construction" data-page={route} data-nav-collapsed={sidebarCollapsed} data-nav-open={constructionNavOpen} data-project-open={constructionProjectOpen} data-screen-preview={constructionFinanceRoute ? financeScreenPreview : undefined} data-login-preview={screenPreview.active ? session.role : undefined}>
    <section className="hs-shell" aria-label="시공 반응형 화면">
      {workspaceTopbar}

      <nav id="construction-global-nav" ref={constructionNavRef} className="hs-global-nav" aria-label="전체 메뉴">
        <div className="hs-nav-context"><span>CONSTRUCTION</span><strong>시공</strong><small>{MOBILE_ROUTE_SUBTITLE[route]}</small></div>
        <div className="hs-nav-body">
          {DESKTOP_NAV_GROUPS.map((group) => {
            const groupItems = visibleNavigation.filter(
              (item) => group.ids.includes(item.id) && !item.mobileOnly,
            );
            if (!groupItems.length) return null;
            return (
              <div className="nav-group" key={group.label ?? group.ids[0]}>
                {group.label && <span className="nav-section-heading" aria-hidden="true">{group.label}</span>}
                {groupItems.map((item) => {
                  const actionCount = item.id === "approvals"
                    ? approvalCount
                    : item.id === "payments"
                      ? customerReceiptActionCount
                      : item.id === "payouts"
                        ? vendorPayoutActionCount
                        : 0;
                  const actionLabel = item.id === "approvals"
                    ? "승인 필요"
                    : item.id === "payments"
                      ? "입금 확인 필요"
                      : "지급 확인 필요";
                  return <a
                    className={`hs-nav-button ${route === item.id ? "is-active" : ""}${item.mobilePrimary ? "" : " is-mobile-secondary"}`}
                    href={`#/${item.id}`}
                    key={item.id}
                    onClick={() => closeConstructionDrawers(false)}
                    aria-label={actionCount > 0
                      ? `${item.label}, ${actionLabel} ${actionCount}건`
                      : item.label}
                    aria-current={route === item.id ? "page" : undefined}
                    title={item.label}
                  >
                    <span className="main-nav__symbol hs-nav-icon" aria-hidden="true"><AppIcon name={item.icon} /></span>
                    <span className="main-nav__label">
                      <span className="main-nav__label-desktop">{item.label}</span>
                      <span className="main-nav__label-mobile">{item.mobileLabel ?? item.label}</span>
                    </span>
                    {actionCount > 0 && (
                      <span className="main-nav__count" aria-label={`${actionLabel} ${actionCount}건`}>
                        {actionCount > 99 ? "99+" : actionCount}
                      </span>
                    )}
                  </a>;
                })}
              </div>
            );
          })}
          {visibleNavigation.filter((item) => item.mobileOnly).map((item) => (
            <a
              className={`hs-nav-button ${route === item.id ? "is-active " : ""}is-mobile-only`}
              href={`#/${item.id}`}
              key={item.id}
              onClick={() => closeConstructionDrawers(false)}
              aria-label={item.label}
              aria-current={route === item.id ? "page" : undefined}
              title={item.label}
            >
              <span className="main-nav__symbol hs-nav-icon" aria-hidden="true"><AppIcon name={item.icon} /></span>
              <span className="main-nav__label">
                <span className="main-nav__label-desktop">{item.label}</span>
                <span className="main-nav__label-mobile">{item.mobileLabel ?? item.label}</span>
              </span>
            </a>
          ))}
          <button
            type="button"
            ref={moreTriggerRef}
            className={`main-nav__more-trigger${moreActive || moreOpen ? " is-active" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
          >
            <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="more" /></span>
            <span>더보기</span>
          </button>
        </div>
        <button
          type="button"
          className="hs-nav-collapse"
          aria-pressed={sidebarCollapsed}
          aria-label={sidebarCollapsed ? "시공 사이드바 펼치기" : "시공 사이드바 접기"}
          onClick={() => setSidebarCollapsed((current) => !current)}
        >
          <span aria-hidden="true">{sidebarCollapsed ? "›" : "‹"}</span>
          <span>{sidebarCollapsed ? "펼치기" : "사이드바 접기"}</span>
        </button>
      </nav>

      {moreOpen && (
        <div className="mobile-more-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setMoreOpen(false);
        }}>
          <section ref={moreSheetRef} className="mobile-more-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-more-title">
            <header>
              <div><p className="eyebrow">MORE</p><h2 id="mobile-more-title">더보기</h2></div>
              <button type="button" className="dialog-close" onClick={() => setMoreOpen(false)} aria-label="더보기 닫기">×</button>
            </header>
            <nav aria-label="추가 메뉴">
              <a className={route === "sms" ? "is-active" : ""} href="#/sms" onClick={() => setMoreOpen(false)}>
                <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="sms" /></span>
                <span><strong>문자함</strong><small>고객 문자 작성·발송</small></span>
                <span aria-hidden="true">›</span>
              </a>
              <div className={`mobile-more-finance${route === "payments" || (route === "payouts" && canAccessPayouts) ? " is-active" : ""}`}>
                <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="payments" /></span>
                <span><strong>입출금</strong><small>고객입금·업체지급 관리</small></span>
                <span className="mobile-more-finance__links">
                  <a href="#/payments" onClick={() => setMoreOpen(false)}>받은 돈</a>
                  {canAccessPayouts && <a href="#/payouts" onClick={() => setMoreOpen(false)}>줄 돈</a>}
                </span>
              </div>
              <a className={route === "reports" ? "is-active" : ""} href="#/reports" onClick={() => setMoreOpen(false)}>
                <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="reports" /></span>
                <span><strong>월간 보고</strong><small>월별 매출·마진·정산 확인</small></span>
                <span aria-hidden="true">›</span>
              </a>
              <a className={route === "estimates" ? "is-active" : ""} href="#/estimates" onClick={() => setMoreOpen(false)}>
                <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="reports" /></span>
                <span><strong>견적</strong><small>빠른 계산·가견적·정식 견적</small></span>
                <span aria-hidden="true">›</span>
              </a>
              <a className={route === "finance" ? "is-active" : ""} href="#/finance" onClick={() => setMoreOpen(false)}>
                <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="payments" /></span>
                <span><strong>자금관리</strong><small>예정 입출금·통장·매칭</small></span>
                <span aria-hidden="true">›</span>
              </a>
              {!financeAdministrator && financeCapabilities.canConfirmMoney && (
                <a className={route === "approvals" ? "is-active" : ""} href="#/approvals" onClick={() => setMoreOpen(false)}>
                  <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="approvals" /></span>
                  <span><strong>견적 승인</strong><small>최종 승인 대기 {approvalCount}건</small></span>
                  {approvalCount > 0 ? <b className="mobile-more__count">{approvalCount > 99 ? "99+" : approvalCount}</b> : <span aria-hidden="true">›</span>}
                </a>
              )}
              {!financeAdministrator && <a className={route === "operations" ? "is-active" : ""} href="#/operations" onClick={() => setMoreOpen(false)}>
                <span className="main-nav__symbol" aria-hidden="true"><AppIcon name="operations" /></span>
                <span><strong>운영</strong><small>공종·업체·회사정보</small></span>
                <span aria-hidden="true">›</span>
              </a>}
            </nav>
          </section>
        </div>
      )}

      {route === "projects" || constructionFinanceRoute ? <aside id="construction-project-rail" ref={setConstructionRailTarget} className="hs-project-rail hs-construction-project-rail" aria-label={route === "projects" ? "시공 프로젝트 목록" : "재무 세부 메뉴"} /> : null}

      <main className="hs-workspace">
        <div className="hs-mobile-controls">
          <button type="button" aria-expanded={constructionNavOpen} aria-controls="construction-global-nav" onClick={(event) => {
            if (constructionNavOpen) closeConstructionDrawers(true);
            else {
              constructionDrawerReturnFocusRef.current = event.currentTarget;
              setConstructionNavOpen(true);
              setConstructionProjectOpen(false);
            }
          }}>전체 메뉴</button>
          {route === "projects" || constructionFinanceRoute ? <button type="button" aria-expanded={constructionProjectOpen} aria-controls="construction-project-rail" onClick={(event) => {
            if (constructionProjectOpen) closeConstructionDrawers(true);
            else {
              constructionDrawerReturnFocusRef.current = event.currentTarget;
              setConstructionProjectOpen(true);
              setConstructionNavOpen(false);
            }
          }}>{route === "projects" ? "프로젝트 목록" : "세부 메뉴"}</button> : null}
        </div>
        <div className="hs-content">
        <div className="hs-functional-island" data-functional-island={`construction-${route}`}>
          {screenPreview.selectedManager ? <aside className="admin-screen-preview-banner" role="status" aria-live="polite">
            <span><AppIcon name="admin" /><strong>{screenPreview.selectedManager.name}</strong> 로그인 화면 보는 중</span>
            <small>실제 로그인과 작업 기록은 셀모 계정으로 유지됩니다.</small>
            <button type="button" onClick={screenPreview.reset}>셀모 화면으로 돌아가기</button>
          </aside> : null}
          <DeviceDeliveryAlertBanner session={session} />
          <FinanceCapabilitiesOverrideProvider capabilities={screenPreview.financeCapabilities}>
          <Suspense fallback={<PageFallback />}>
          {route === "dashboard" && <HomeDashboardPage {...pageProps} />}
          {route === "my-work" && <ConstructionMyWorkPage {...pageProps} />}
          {route === "projects" && <ProjectWorkspace {...pageProps} externalSearch={constructionSearch} railPortalTarget={constructionRailTarget} onRailProjectSelect={() => closeConstructionDrawers(false)} onSyncRefresh={() => void loadSync()} />}
          {route === "schedule" && <MobileSchedulePage {...pageProps} />}
          {constructionFinanceRoute && <ConstructionFinanceWorkspace route={route} railPortalTarget={constructionRailTarget} canViewBankLedger={financeCapabilities.canManageBankLedger} canConfirmMoney={financeCapabilities.canConfirmMoney} previewMode={financeScreenPreview} onPreviewMode={setFinanceScreenPreview} onRailNavigate={() => closeConstructionDrawers(false)} />}
          {route === "payments" && <ReceiptsPage {...pageProps} />}
          {route === "payouts" && (financeCapabilitiesLoading
            ? <PageFallback />
            : canAccessPayouts ? <PayoutsPage {...pageProps} /> : null)}
          {route === "as-expenses" && <AsExpensesPage />}
          {route === "sms" && <SmsPage {...pageProps} />}
          {route === "operations" && <OperationsPage {...pageProps} />}
          {route === "improvements" && <ImprovementCenterPage {...pageProps} />}
          {route === "approvals" && financeCapabilities.canConfirmMoney && <ApprovalsPage {...pageProps} />}
          {route === "admin" && session.role === "ADMIN" && <AdminPage {...pageProps} />}
          </Suspense>
          </FinanceCapabilitiesOverrideProvider>
        </div>
        </div>
        <FeedbackLauncher />
      </main>
    </section>
    {(constructionNavOpen || constructionProjectOpen) ? <button className="hs-drawer-backdrop" type="button" aria-label="열린 시공 메뉴 닫기" onClick={() => closeConstructionDrawers(true)} /> : null}
    </div>
  );
}

function AuthenticatedApp({ session, loggingOut, onLogout }: WorkspaceProps) {
  const [homeStyleRegistration, setHomeStyleRegistration] = useState(
    () => isHomeStyleRegistrationHash(window.location.hash),
  );
  const acceptedHashRef = useRef(window.location.hash);
  const historyTraversalRef = useRef(false);

  useEffect(() => {
    const markHistoryTraversal = () => {
      historyTraversalRef.current = window.location.hash !== acceptedHashRef.current;
    };
    const applyRoute = () => {
      const nextHash = window.location.hash;
      const historyTraversal = historyTraversalRef.current;
      historyTraversalRef.current = false;
      const routeChange = new CustomEvent("selmo:before-hash-route", {
        cancelable: true,
        detail: { nextHref: window.location.href, historyTraversal },
      });
      if (!window.dispatchEvent(routeChange)) {
        const acceptedUrl = `${window.location.pathname}${window.location.search}${acceptedHashRef.current}`;
        window.history.pushState(window.history.state, "", acceptedUrl);
        return;
      }
      acceptedHashRef.current = nextHash;
      setHomeStyleRegistration(isHomeStyleRegistrationHash(nextHash));
    };
    window.addEventListener("popstate", markHistoryTraversal);
    window.addEventListener("hashchange", applyRoute);
    return () => {
      window.removeEventListener("popstate", markHistoryTraversal);
      window.removeEventListener("hashchange", applyRoute);
    };
  }, []);

  if (isHomeStyleFieldRole(session.role)) {
    return <FieldWorkApp session={session} loggingOut={loggingOut} onLogout={onLogout} />;
  }

  if (homeStyleRegistration) {
    return <ProtectedHomeStyleRegistration session={session} loggingOut={loggingOut} onLogout={onLogout} />;
  }

  return <OperationalWorkspace session={session} loggingOut={loggingOut} onLogout={onLogout} />;
}

export default function App() {
  return (
    <AuthGate>
      {(session, logout, loggingOut) => (
        <StaffDesignBoundary><AuthenticatedApp session={session} onLogout={logout} loggingOut={loggingOut} /></StaffDesignBoundary>
      )}
    </AuthGate>
  );
}
